import React from "react";
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CandidateSourceId,
  CandidateSourceMetadata,
  RecommendationEngineSourceConfig,
} from "../../../../types/recommendationEngine";
import {
  CANDIDATE_SOURCE_LABELS,
  isValidRadiusKm,
} from "../../../../utils/recommendationEngine/constants";
import {
  CANDIDATE_SOURCE_FIELD_TOOLTIPS,
  CANDIDATE_SOURCE_HELP,
} from "../copy";
import { engineTokens } from "../theme";
import InfoTooltip from "./InfoTooltip";

type Props = {
  source: RecommendationEngineSourceConfig;
  metadata?: CandidateSourceMetadata;
  onChange: (next: RecommendationEngineSourceConfig) => void;
  /** Shown in place of the built-in param fields; the radius field stays. */
  customParams?: React.ReactNode;
};

const numberToOptional = (value: string): number | undefined =>
  value === "" ? undefined : Number(value);

const CandidateSourceCard = ({
  source,
  metadata,
  onChange,
  customParams,
}: Props) => {
  const sourceId = source.sourceId as CandidateSourceId;
  const help = CANDIDATE_SOURCE_HELP[sourceId];
  const label = metadata?.label || CANDIDATE_SOURCE_LABELS[sourceId];
  const plain = metadata?.summary || help?.plain || "";
  const tooltip =
    metadata?.description || help?.tooltip || metadata?.summary || "";
  const paramMetadataByName = React.useMemo(() => {
    const map = new Map<string, NonNullable<typeof metadata>["params"][number]>();
    metadata?.params?.forEach((param) => {
      map.set(param.name, param);
    });
    return map;
  }, [metadata]);
  const tooltipFor = (paramName: string, fallback: string) =>
    paramMetadataByName.get(paramName)?.description || fallback;

  const updateParam = (patch: Record<string, unknown>) => {
    onChange({
      ...source,
      params: {
        ...((source.params ?? {}) as Record<string, unknown>),
        ...patch,
      },
    } as RecommendationEngineSourceConfig);
  };

  const renderParams = (): React.ReactNode => {
    if (source.sourceId === "image_ann") {
      return (
        <>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              size="small"
              type="number"
              label="Min image score"
              value={source.params?.minImageScore ?? ""}
              onChange={(e) =>
                updateParam({
                  minImageScore: numberToOptional(e.target.value),
                })
              }
              sx={{ width: 160 }}
            />
            <InfoTooltip
              title={tooltipFor(
                "minImageScore",
                CANDIDATE_SOURCE_FIELD_TOOLTIPS.minImageScore,
              )}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(source.params?.onlyOnShoppingLists)}
                  onChange={(e) =>
                    updateParam({ onlyOnShoppingLists: e.target.checked })
                  }
                />
              }
              label="Only on shopping lists"
            />
            <InfoTooltip
              title={tooltipFor(
                "onlyOnShoppingLists",
                CANDIDATE_SOURCE_FIELD_TOOLTIPS.onlyOnShoppingLists,
              )}
            />
          </Box>
        </>
      );
    }

    if (source.sourceId === "shopping_list_cooccurrence") {
      return (
        <>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              size="small"
              type="number"
              label="Anchor cap"
              value={source.params?.anchorCap ?? ""}
              onChange={(e) =>
                updateParam({ anchorCap: numberToOptional(e.target.value) })
              }
              sx={{ width: 120 }}
            />
            <InfoTooltip
              title={tooltipFor(
                "anchorCap",
                CANDIDATE_SOURCE_FIELD_TOOLTIPS.anchorCap,
              )}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              size="small"
              type="number"
              label="Anchor min image score"
              value={source.params?.anchorMinImageScore ?? ""}
              onChange={(e) =>
                updateParam({
                  anchorMinImageScore: numberToOptional(e.target.value),
                })
              }
              sx={{ width: 200 }}
            />
            <InfoTooltip
              title={tooltipFor(
                "anchorMinImageScore",
                CANDIDATE_SOURCE_FIELD_TOOLTIPS.anchorMinImageScore,
              )}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={source.params?.useExactNameAnchors ?? false}
                  onChange={(e) =>
                    updateParam({ useExactNameAnchors: e.target.checked })
                  }
                />
              }
              label="Exact-name anchors"
            />
            <InfoTooltip
              title={tooltipFor(
                "useExactNameAnchors",
                CANDIDATE_SOURCE_FIELD_TOOLTIPS.useExactNameAnchors,
              )}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={source.params?.useImageAnchors ?? false}
                  onChange={(e) =>
                    updateParam({ useImageAnchors: e.target.checked })
                  }
                />
              }
              label="Image anchors"
            />
            <InfoTooltip
              title={tooltipFor(
                "useImageAnchors",
                CANDIDATE_SOURCE_FIELD_TOOLTIPS.useImageAnchors,
              )}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={source.params?.useEsFuzzyAnchors ?? false}
                  onChange={(e) =>
                    updateParam({ useEsFuzzyAnchors: e.target.checked })
                  }
                />
              }
              label="Elasticsearch fuzzy anchors"
            />
            <InfoTooltip
              title={tooltipFor(
                "useEsFuzzyAnchors",
                CANDIDATE_SOURCE_FIELD_TOOLTIPS.useEsFuzzyAnchors,
              )}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    source.params?.anchorsRequireOnShoppingLists ?? false
                  }
                  onChange={(e) =>
                    updateParam({
                      anchorsRequireOnShoppingLists: e.target.checked,
                    })
                  }
                />
              }
              label="Anchors require shopping list"
            />
            <InfoTooltip
              title={tooltipFor(
                "anchorsRequireOnShoppingLists",
                CANDIDATE_SOURCE_FIELD_TOOLTIPS.anchorsRequireOnShoppingLists,
              )}
            />
          </Box>
        </>
      );
    }

    if (source.sourceId === "trending_items") {
      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TextField
            size="small"
            type="number"
            label="Hours window"
            value={source.params?.hoursWindow ?? ""}
            onChange={(e) =>
              updateParam({ hoursWindow: numberToOptional(e.target.value) })
            }
            sx={{ width: 140 }}
          />
          <InfoTooltip
            title={tooltipFor(
              "hoursWindow",
              CANDIDATE_SOURCE_FIELD_TOOLTIPS.hoursWindow,
            )}
          />
        </Box>
      );
    }

    if (
      source.sourceId === "interest_category" ||
      source.sourceId === "interest_promotion_tag" ||
      source.sourceId === "favorite_store"
    ) {
      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TextField
            size="small"
            type="number"
            label="Max matches"
            value={source.params?.maxMatches ?? ""}
            onChange={(e) =>
              updateParam({ maxMatches: numberToOptional(e.target.value) })
            }
            sx={{ width: 140 }}
          />
          <InfoTooltip
            title={tooltipFor(
              "maxMatches",
              CANDIDATE_SOURCE_FIELD_TOOLTIPS.maxMatches,
            )}
          />
        </Box>
      );
    }

    return null;
  };

  const radiusValue = source.params?.radiusKm;
  const radiusInvalid =
    radiusValue !== undefined && !isValidRadiusKm(radiusValue);

  // Every candidate source now accepts a per-source radius bound, so the field
  // is rendered for all sources (not just nearby/geo/trending).
  const radiusField = (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <TextField
        size="small"
        type="number"
        label="Radius (km)"
        value={radiusValue ?? ""}
        onChange={(e) =>
          updateParam({ radiusKm: numberToOptional(e.target.value) })
        }
        error={radiusInvalid}
        helperText={radiusInvalid ? "0 < km ≤ 100" : undefined}
        sx={{ width: 140 }}
      />
      <InfoTooltip
        title={tooltipFor("radiusKm", CANDIDATE_SOURCE_FIELD_TOOLTIPS.radiusKm)}
      />
    </Box>
  );

  const paramsContent = (
    <>
      {customParams ?? renderParams()}
      {radiusField}
    </>
  );

  return (
    <Box
      sx={{
        border: `1px solid ${engineTokens.border}`,
        borderRadius: 2,
        p: 1.5,
        mb: 1.5,
        backgroundColor: source.enabled
          ? engineTokens.bgPaper
          : engineTokens.bgMuted,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={source.enabled}
              onChange={(e) =>
                onChange({
                  ...source,
                  enabled: e.target.checked,
                } as RecommendationEngineSourceConfig)
              }
            />
          }
          label={
            <Box sx={{ display: "inline-flex", alignItems: "center" }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {label}
              </Typography>
              <InfoTooltip title={tooltip} />
            </Box>
          }
        />
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TextField
            size="small"
            type="number"
            label="Cap"
            value={source.cap}
            onChange={(e) =>
              onChange({
                ...source,
                cap: Number(e.target.value),
              } as RecommendationEngineSourceConfig)
            }
            sx={{ width: 110 }}
          />
          <InfoTooltip title={CANDIDATE_SOURCE_FIELD_TOOLTIPS.cap} />
        </Box>
        {!source.enabled && (
          <Chip size="small" label="Disabled" variant="outlined" />
        )}
      </Box>

      {plain && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5, ml: 4 }}
        >
          {plain}
        </Typography>
      )}

      {paramsContent && (
        <Box
          sx={{
            mt: 1.5,
            ml: 4,
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
          }}
        >
          {paramsContent}
        </Box>
      )}

      {metadata &&
        (metadata.requiredContext.length > 0 ||
          metadata.relatedSignals.length > 0 ||
          metadata.dataUsed.length > 0 ||
          metadata.howCandidatesAreFound) && (
          <Box
            sx={{
              mt: 1.5,
              ml: 4,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            {metadata.requiredContext.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Requires:
                </Typography>
                {metadata.requiredContext.map((ctx) => (
                  <Chip
                    key={ctx}
                    label={ctx}
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 18, fontSize: 11 }}
                  />
                ))}
              </Box>
            )}
            {metadata.relatedSignals.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Related signals:
                </Typography>
                {metadata.relatedSignals.map((sig) => (
                  <Chip
                    key={sig}
                    label={sig}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: 11 }}
                  />
                ))}
              </Box>
            )}
            {metadata.dataUsed.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Data used: {metadata.dataUsed.join(", ")}
              </Typography>
            )}
            {metadata.howCandidatesAreFound && (
              <Tooltip title={metadata.howCandidatesAreFound} placement="top" arrow>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    fontStyle: "italic",
                    cursor: "help",
                  }}
                >
                  How this finds candidates: hover for details
                </Typography>
              </Tooltip>
            )}
          </Box>
        )}
    </Box>
  );
};

export default CandidateSourceCard;
